import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ExternalLink, LogOut, Menu, LayoutDashboard, Send, Layers, ListChecks, Tag, Image, MessageSquareQuote, HelpCircle,
  Gift, Clock, Activity, Building2, Phone, ShieldCheck,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { http } from "@/lib/api";
import { SettingsPanel } from "@/components/admin/SettingsPanel";
import { CategoriesPanel } from "@/components/admin/CategoriesPanel";
import { PricesPanel } from "@/components/admin/PricesPanel";
import { PhotosPanel } from "@/components/admin/PhotosPanel";
import { TestimonialsPanel } from "@/components/admin/TestimonialsPanel";
import { AccountPanel } from "@/components/admin/AccountPanel";
import { PackagesPanel } from "@/components/admin/PackagesPanel";
import { HoursPanel } from "@/components/admin/HoursPanel";
import { PublishPanel } from "@/components/admin/PublishPanel";
import { FaqPanel } from "@/components/admin/FaqPanel";
import { OverviewPanel } from "@/components/admin/OverviewPanel";

export const NAV = [
  ["Dashboard", [["overview", "Ringkasan", LayoutDashboard], ["publish", "Draft & Publish", Send]]],
  ["Manajemen Layanan", [["kategori", "Kategori Layanan", Layers], ["detail", "Detail & Highlight", ListChecks], ["harga", "Daftar Harga", Tag], ["dokumentasi", "Dokumentasi", Image]]],
  ["Marketing & Konten", [["testimoni", "Testimoni Pasien", MessageSquareQuote], ["faq", "FAQ", HelpCircle], ["paket", "Paket & Promo", Gift]]],
  ["Operasional", [["jadwal", "Jadwal Operasional", Clock], ["status", "Status Layanan", Activity]]],
  ["Pengaturan", [["profil", "Profil Nanina Care", Building2], ["kontak", "Kontak & Media Sosial", Phone], ["akun", "Keamanan Akun", ShieldCheck]]],
];
const LABEL = Object.fromEntries(NAV.flatMap(([, items]) => items.map(([k, l]) => [k, l])));

const NavList = ({ active, onPick }) => (
  <nav className="space-y-5" aria-label="Menu admin">
    {NAV.map(([group, items]) => (
      <div key={group}>
        <p className="px-3 text-[10px] font-bold uppercase tracking-[0.14em] text-nanina-muted">{group}</p>
        <ul className="mt-1.5 space-y-0.5">
          {items.map(([k, l, Icon]) => (
            <li key={k}>
              <button onClick={() => onPick(k)} className={`w-full flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${active === k ? "bg-nanina-green text-white" : "text-nanina-green hover:bg-nanina-skysoft/60"}`} data-testid={`admin-nav-${k}`}>
                <Icon className="h-4 w-4 shrink-0" /> {l}
              </button>
            </li>
          ))}
        </ul>
      </div>
    ))}
  </nav>
);

const Brand = () => (
  <div className="flex items-center gap-2.5">
    <img src="/logo-nanina.png" alt="Logo Nanina Care" className="h-9 w-9" />
    <div className="leading-tight">
      <p className="text-sm font-extrabold text-nanina-green">NANINA CARE</p>
      <p className="text-[11px] text-nanina-muted">Dashboard Admin</p>
    </div>
  </div>
);

const Panel = ({ menu, data, reload, user, go }) => {
  const c = data.categories;
  switch (menu) {
    case "publish": return <PublishPanel data={data} reload={reload} />;
    case "kategori": return <CategoriesPanel categories={c} reload={reload} />;
    case "detail": return <CategoriesPanel categories={c} reload={reload} mode="detail" />;
    case "harga": return <PricesPanel categories={c} treatments={data.treatments} reload={reload} />;
    case "dokumentasi": return <PhotosPanel categories={c} photos={data.photos} reload={reload} />;
    case "testimoni": return <TestimonialsPanel categories={c} testimonials={data.testimonials} reload={reload} />;
    case "faq": return <FaqPanel faqs={data.faqs} reload={reload} />;
    case "paket": return <PackagesPanel categories={c} packages={data.packages} reload={reload} />;
    case "jadwal": return <HoursPanel categories={c} mode="jadwal" />;
    case "status": return <HoursPanel categories={c} mode="status" />;
    case "profil": return <SettingsPanel categories={c} section="profil" />;
    case "kontak": return <SettingsPanel categories={c} section="kontak" />;
    case "akun": return <AccountPanel user={user} />;
    default: return <OverviewPanel data={data} go={go} />;
  }
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [params, setParams] = useSearchParams();
  const [navOpen, setNavOpen] = useState(false);
  const menu = LABEL[params.get("menu")] ? params.get("menu") : "overview";
  const go = (k) => { setParams({ menu: k }); setNavOpen(false); window.scrollTo(0, 0); };

  const reload = useCallback(async () => {
    const [c, t, p, ts, pk, fq] = await Promise.all([
      http.get("/admin/categories"), http.get("/admin/treatments"), http.get("/admin/photos"), http.get("/admin/testimonials"), http.get("/admin/packages"), http.get("/admin/faqs"),
    ]);
    setData({ categories: c.data, treatments: t.data, photos: p.data, testimonials: ts.data, packages: pk.data, faqs: fq.data });
  }, []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="min-h-screen bg-nanina-bg" data-testid="admin-dashboard">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 z-30 w-64 flex-col bg-white border-r border-nanina-line" data-testid="admin-sidebar">
        <div className="h-16 px-4 flex items-center border-b border-nanina-line"><Brand /></div>
        <div className="flex-1 overflow-y-auto p-3"><NavList active={menu} onPick={go} /></div>
      </aside>
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="w-72 p-4 overflow-y-auto" data-testid="admin-mobile-nav">
          <SheetTitle className="sr-only">Menu admin</SheetTitle>
          <SheetDescription className="sr-only">Navigasi dashboard admin</SheetDescription>
          <div className="mb-5"><Brand /></div>
          <NavList active={menu} onPick={go} />
        </SheetContent>
      </Sheet>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-nanina-line">
          <div className="px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <button onClick={() => setNavOpen(true)} className="lg:hidden p-2 -ml-2 rounded-lg text-nanina-green hover:bg-nanina-bg" aria-label="Buka menu" data-testid="admin-mobile-menu-button"><Menu className="h-5 w-5" /></button>
              <h1 className="text-base sm:text-lg font-extrabold tracking-tight text-nanina-green truncate" data-testid="admin-page-title">{LABEL[menu]}</h1>
            </div>
            <div className="flex items-center gap-1">
              <Link to="/admin/preview" target="_blank" className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold text-nanina-green hover:bg-nanina-bg" data-testid="admin-preview-link">Pratinjau</Link>
              <Link to="/" target="_blank" className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold text-nanina-green hover:bg-nanina-bg" data-testid="admin-view-site-link"><ExternalLink className="h-4 w-4" /><span className="hidden sm:inline">Lihat website</span></Link>
              <button onClick={logout} className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /><span className="hidden sm:inline">Keluar</span></button>
            </div>
          </div>
        </header>
        <main className="px-4 sm:px-6 py-6 max-w-5xl">
          {!data ? <p className="text-sm text-nanina-muted" data-testid="admin-loading">Memuat data...</p> : <Panel menu={menu} data={data} reload={reload} user={user} go={go} />}
        </main>
      </div>
    </div>
  );
}
